import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Package, ChevronDown, ShoppingBag } from 'lucide-react';
import { useAuth, OrderStatus } from '../context/AuthContext';
const statusStyles: Record<OrderStatus, {
  label: string;
  className: string;
}> = {
  processing: {
    label: 'Processing',
    className: 'bg-amber-50 text-amber-700 border-amber-200'
  },
  shipped: {
    label: 'Shipped',
    className: 'bg-blue-50 text-blue-700 border-blue-200'
  },
  delivered: {
    label: 'Delivered',
    className: 'bg-green-50 text-green-700 border-green-200'
  },
  cancelled: {
    label: 'Cancelled',
    className: 'bg-gray-50 text-gray-600 border-gray-200'
  }
};
export function Orders() {
  const {
    user
  } = useAuth();
  const [openOrder, setOpenOrder] = useState<string | null>(null);
  const orders = user?.orders || [];
  return <div data-id="element-616">
      <h2 className="text-xl font-bold text-gray-900 mb-1" data-id="element-617">My Orders</h2>
      <p className="text-sm text-gray-500 mb-6" data-id="element-618">
        View past orders and track their status.
      </p>

      {orders.length === 0 ? <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center" data-id="element-619">
          <div className="w-14 h-14 bg-gray-50 rounded-full flex items-center justify-center text-gray-300 mx-auto mb-4" data-id="element-620">
            <ShoppingBag className="w-7 h-7" data-id="element-621" />
          </div>
          <h3 className="font-medium text-gray-900 mb-1" data-id="element-622">No orders yet</h3>
          <p className="text-sm text-gray-500 mb-6" data-id="element-623">
            When you place your first order, it will show up here.
          </p>
          <Link to="/shop" className="inline-flex items-center px-6 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors" data-id="element-624">
            Start Shopping
          </Link>
        </div> : <div className="space-y-4" data-id="element-625">
          {orders.map(order => {
        const status = statusStyles[order.status];
        const isOpen = openOrder === order.id;
        return <div key={order.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden" data-id="element-626">
                <button type="button" onClick={() => setOpenOrder(isOpen ? null : order.id)} className="w-full p-5 flex items-center justify-between gap-4 text-left hover:bg-gray-50 transition-colors" data-id="element-627">
                  <div className="flex items-center gap-4" data-id="element-628">
                    <div className="w-10 h-10 rounded-lg bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0" data-id="element-629">
                      <Package className="w-5 h-5" data-id="element-630" />
                    </div>
                    <div data-id="element-631">
                      <div className="font-semibold text-gray-900" data-id="element-632">
                        Order {order.id}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-633">
                        {new Date(order.date).toLocaleDateString('en-MV', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                        {' • '}
                        {order.items.length}{' '}
                        {order.items.length === 1 ? 'item' : 'items'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4" data-id="element-634">
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full border ${status.className}`} data-id="element-635">
                      {status.label}
                    </span>
                    <div className="text-sm font-semibold text-gray-900 hidden sm:block" data-id="element-636">
                      MVR {order.total.toFixed(2)}
                    </div>
                    <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-637" />
                  </div>
                </button>

                {isOpen && <div className="border-t border-gray-100 p-5 bg-gray-50 space-y-4" data-id="element-638">
                    <ul className="divide-y divide-gray-200 bg-white rounded-lg border border-gray-100" data-id="element-639">
                      {order.items.map((item, idx) => <li key={idx} className="p-3 flex items-center gap-3" data-id="element-640">
                          <div className="w-14 h-14 rounded-lg bg-gray-100 overflow-hidden border border-gray-200 flex-shrink-0" data-id="element-641">
                            <img src={item.productImage} alt={item.productName} className="w-full h-full object-cover" data-id="element-642" />
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-643">
                            <div className="text-sm font-medium text-gray-900 line-clamp-1" data-id="element-644">
                              {item.productName}
                            </div>
                            <div className="text-xs text-gray-500" data-id="element-645">
                              {item.selectedSize && <span data-id="element-646">Size: {item.selectedSize} • </span>}
                              {item.selectedColor && <span data-id="element-647">Color: {item.selectedColor} • </span>}
                              Qty: {item.quantity}
                            </div>
                            {item.selectedAddOns.length > 0 && <div className="text-xs text-gray-400 mt-0.5" data-id="element-648">
                                + {item.selectedAddOns.join(', ')}
                              </div>}
                          </div>
                          <div className="text-sm font-medium text-gray-900" data-id="element-649">
                            MVR {(item.unitPrice * item.quantity).toFixed(2)}
                          </div>
                        </li>)}
                    </ul>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm" data-id="element-650">
                      <div className="bg-white p-4 rounded-lg border border-gray-100" data-id="element-651">
                        <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2" data-id="element-652">
                          Shipping Address
                        </div>
                        <div className="text-gray-900" data-id="element-653">
                          {order.shippingAddress.firstName}{' '}
                          {order.shippingAddress.lastName}
                        </div>
                        <div className="text-gray-600" data-id="element-654">
                          {order.shippingAddress.address}
                        </div>
                        <div className="text-gray-600" data-id="element-655">
                          {order.shippingAddress.island}
                          {order.shippingAddress.postalCode && `, ${order.shippingAddress.postalCode}`}
                        </div>
                      </div>
                      <div className="bg-white p-4 rounded-lg border border-gray-100 space-y-1" data-id="element-656">
                        <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2" data-id="element-657">
                          Order Summary
                        </div>
                        <div className="flex justify-between text-gray-600" data-id="element-658">
                          <span data-id="element-659">Subtotal</span>
                          <span data-id="element-660">MVR {order.subtotal.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-gray-600" data-id="element-661">
                          <span data-id="element-662">Shipping</span>
                          <span data-id="element-663">
                            {order.shipping === 0 ? 'Free' : `MVR ${order.shipping.toFixed(2)}`}
                          </span>
                        </div>
                        <div className="flex justify-between font-semibold text-gray-900 pt-2 border-t border-gray-100" data-id="element-664">
                          <span data-id="element-665">Total</span>
                          <span data-id="element-666">MVR {order.total.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>}
              </div>;
      })}
        </div>}
    </div>;
}